// libraries/components/src/components/shared/upcoming-trips.ts
import { withManageTripsNextClientHeaders } from "@jb-manage-trips/api/manage-trips-client-headers";
import { createManageTripsHttpError } from "@jb-manage-trips/fe-logger/manage-trips-events";
import type { UpcomingTripsApiResponse } from "@jb-manage-trips/types/itinerary";

// React Query key shared by auth flow + itinerary store
export const UPCOMING_TRIPS_QUERY_KEY = ["upcoming-trips-itinerary"] as const;
export const USE_UPCOMING_TRIPS_MOCKS =
  // eslint-disable-next-line no-implicit-coercion -- prefer this pattern
  !!process.env.NEXT_PUBLIC_USE_UPCOMING_TRIPS_MOCKS &&
  process.env.NEXT_PUBLIC_USE_UPCOMING_TRIPS_MOCKS.toLowerCase() === "true";
// Local mock endpoint served by docker-compose (single source of truth)
export const UPCOMING_TRIPS_MOCK_URL =
  process.env.NEXT_PUBLIC_ITINERARY_MOCK_URL ?? "";
/**
 * Shared fetcher for upcoming trips / reservations-by-TB.
 *
 * - If USE_UPCOMING_TRIPS_MOCKS is true, hits CB mock JSON API (might be local, might be network) irrespective of auth.
 * - Otherwise, calls the live URL with Authorization header.
 */
export async function fetchUpcomingTrips(parameters: {
  url: string;
  accessToken?: string;
}): Promise<UpcomingTripsApiResponse> {
  const { url, accessToken } = parameters;

  if (USE_UPCOMING_TRIPS_MOCKS) {
    const response = await fetch(UPCOMING_TRIPS_MOCK_URL);

    if (!response.ok) {
      const txt = await response.text().catch(() => "");
      throw createManageTripsHttpError({
        errorCode: `UPCOMING_TRIPS_MOCK_HTTP_${String(response.status)}`,
        message:
          `[fetchUpcomingTrips] mock upcoming trips failed: ${String(response.status)} ${txt}`.trim(),
        status: response.status,
      });
    }

    return (await response.json()) as UpcomingTripsApiResponse;
  }

  if (!accessToken) {
    throw createManageTripsHttpError({
      errorCode: "UPCOMING_TRIPS_FETCH_MISSING_ACCESS_TOKEN",
      message:
        "[fetchUpcomingTrips] accessToken is required when not in mock mode",
    });
  }

  const response = await fetch(url, {
    headers: withManageTripsNextClientHeaders({
      Authorization: `Bearer ${accessToken}`,
    }),
  });

  if (!response.ok) {
    const txt = await response.text().catch(() => "");
    throw createManageTripsHttpError({
      errorCode: `UPCOMING_TRIPS_FETCH_HTTP_${String(response.status)}`,
      message:
        `[fetchUpcomingTrips] live upcoming trips failed: ${String(response.status)} ${txt}`.trim(),
      status: response.status,
    });
  }

  return (await response.json()) as UpcomingTripsApiResponse;
}
